import { FC, Fragment, useState } from 'react';
import {
	Button,
	Col,
	Divider,
	Form,
	Input,
	Row,
	Table,
	Typography,
} from 'antd';
import { DeleteOutlined, EditOutlined } from '@ant-design/icons';
import { useForm } from '@/libs/antd/form';
import ANTD_DEFAULT_PROPS from '@/libs/antd/props';
import FleetModal from '@/libs/antd/components/Modal';
import FleetTitle from '@/libs/antd/components/Title';
import rule from '@/libs/antd/rules';
import { OfficeObservationAttendee } from '@/libs/fleet-api/services/types';
import { useMutation } from '@tanstack/react-query';
import {
	addCCASiteReportAttendee,
	deleteCCASiteReportAttendee,
	updateCCASiteReportAttendee,
} from '@/libs/fleet-api/services/visit-reports';
import { useCCAVisitReportVerificationPage } from './VisitReportVerificationPage';
import { useModals } from '@/components/modals/ModalProvider';

type VisitReportAttendeeForm = {
	input?: OfficeObservationAttendee<'payload'>;
	lists: OfficeObservationAttendee<'payload'>[];
};

const CCASiteVisitReportAttendee: FC = () => {
	const { siteReport, surveyInformation } = useCCAVisitReportVerificationPage();

	const { form, fieldName } = useForm<VisitReportAttendeeForm>();
	const [modal, setModal] = useState<
		{ type: 'add' } | { type: 'edit'; index: number } | null
	>(null);

	const addCCASiteReportAttendeeMutation = useMutation({
		mutationFn: addCCASiteReportAttendee,
	});

	const updateCCASiteReportAttendeeMutation = useMutation({
		mutationFn: updateCCASiteReportAttendee,
	});

	const deleteCCASiteReportAttendeeMutation = useMutation({
		mutationFn: deleteCCASiteReportAttendee,
	});

	const { modal: modalConfirm } = useModals();

	return (
		<Form.Provider>
			<Typography.Title level={3}>Pihak Yang Hadir</Typography.Title>
			<Form
				{...ANTD_DEFAULT_PROPS.FORM}
				form={form}
				initialValues={
					{
						lists: siteReport?.attendees ?? [],
					} as VisitReportAttendeeForm
				}
				onFinish={async ({ input, lists }) => {
					if (modal?.type === 'edit') {
						await updateCCASiteReportAttendeeMutation.mutateAsync(input!);
						form.setFieldsValue({
							lists: lists.map((list, index) => {
								return index === modal.index ? input! : list;
							}),
						});
					} else {
						const res = await addCCASiteReportAttendeeMutation.mutateAsync({
							...input!,
							surveyId: surveyInformation!.id!,
						});
						form.setFieldsValue({
							lists: [...lists, { ...input!, id: res.data.id }],
						});
					}
					setModal(null);
				}}
				style={{ marginBottom: 30 }}
			>
				<Form.Item
					name={fieldName(['input'])}
					noStyle
				/>
				<Form.Item
					name={fieldName(['lists'])}
					noStyle
				/>
				<Form.Item shouldUpdate>
					{({ getFieldsValue, setFieldValue }) => {
						const values = getFieldsValue() as VisitReportAttendeeForm;
						const dataSource =
							values?.lists?.map((list, key?) => {
								return { ...list, key };
							}) ?? [];

						return (
							<Fragment>
								<Row style={{ display: 'flex', justifyContent: 'end' }}>
									<Button
										style={{
											color: '#1557BC',
											borderRadius: 50,
											fontSize: 12,
											height: 24,
											borderColor: '#1557BC',
											fontWeight: 700,
											marginBottom: 20,
										}}
										loading={
											addCCASiteReportAttendeeMutation.isPending ||
											updateCCASiteReportAttendeeMutation.isPending ||
											deleteCCASiteReportAttendeeMutation.isPending
										}
										onClick={() => setModal({ type: 'add' })}
										icon={'+'}
									>
										Add Another
									</Button>
								</Row>
								<Table
									bordered
									pagination={false}
									dataSource={dataSource}
									columns={[
										{
											title: (
												<span
													style={{ display: 'flex', justifyContent: 'center' }}
												>
													Action
												</span>
											),
											render: (_, record, index) => {
												return (
													<Row
														gutter={[40, 20]}
														style={{
															display: 'flex',
															flexWrap: 'nowrap',
															justifyContent: 'center',
															paddingLeft: 20,
															paddingRight: 20,
														}}
													>
														<div
															style={{
																display: 'flex',
																flexWrap: 'nowrap',
																justifyContent: 'center',
																width: 'fit-content',
															}}
														>
															<a
																style={{ display: 'block' }}
																onClick={(e) => {
																	e.preventDefault();
																	setFieldValue(fieldName(['input']), record);
																	setModal({
																		type: 'edit',
																		index,
																	});
																}}
															>
																<EditOutlined />
															</a>
														</div>
														<div
															style={{
																display: 'flex',
																flexWrap: 'nowrap',
																justifyContent: 'center',
															}}
														>
															<Divider type="vertical" />
														</div>
														<div
															style={{
																display: 'flex',
																flexWrap: 'nowrap',
																justifyContent: 'center',
																width: 'fit-content',
															}}
														>
															<a
																style={{ display: 'block' }}
																onClick={async (e) => {
																	e.preventDefault();
																	const confirmDelete =
																		await modalConfirm.confirm({
																			title:
																				'Anda yakin akan menghapus data ini?',
																		});
																	if (confirmDelete) {
																		await deleteCCASiteReportAttendeeMutation.mutateAsync(
																			record.id!,
																		);
																		form.setFieldsValue({
																			lists: values.lists.filter(
																				(_, listIndex) => listIndex !== index,
																			),
																		});
																	}
																}}
															>
																<DeleteOutlined />
															</a>
														</div>
													</Row>
												);
											},
											width: 1,
										},
										{
											title: 'Nama',
											render: (_, record) => record.name ?? '-',
										},
										{
											title: 'Jabatan',
											render: (_, record) => record.position ?? '-',
										},
										{
											title: 'Nama Perusahaan',
											render: (_, record) => record.companyName ?? '-',
										},
									]}
								/>
								<FleetModal
									open={Boolean(modal)}
									width={940}
									closeIcon={false}
									keyboard={false}
									maskClosable={false}
									buttonPosition="end"
									afterClose={() => {
										form.resetFields([fieldName('input')]);
									}}
									onCancel={() => {
										setModal(null);
									}}
									onOk={() => {
										form.submit();
									}}
								>
									<FleetTitle>Add Attendee</FleetTitle>
									<Row gutter={[40, 20]}>
										<Col span={12}>
											<Form.Item
												name={fieldName(['input', 'id'])}
												noStyle
											/>
											<Form.Item
												name={fieldName(['input', 'surveyId'])}
												noStyle
											/>
											<Form.Item
												name={fieldName(['input', 'name'])}
												label="Nama"
												labelCol={{ span: 10 }}
												rules={[rule.required()]}
											>
												<Input />
											</Form.Item>
										</Col>
										<Col span={12}>
											<Form.Item
												name={fieldName(['input', 'position'])}
												label="Jabatan"
												labelCol={{ span: 10 }}
												rules={[rule.required()]}
											>
												<Input />
											</Form.Item>
										</Col>
									</Row>
									<Divider />
									<Row gutter={[40, 20]}>
										<Col span={12}>
											<Form.Item
												name={fieldName(['input', 'companyName'])}
												label="Nama Perusahaan"
												labelCol={{ span: 10 }}
												rules={[rule.required()]}
											>
												<Input />
											</Form.Item>
										</Col>
									</Row>
									<Divider />
								</FleetModal>
							</Fragment>
						);
					}}
				</Form.Item>
			</Form>
		</Form.Provider>
	);
};

export default CCASiteVisitReportAttendee;
